import React, { useState } from "react";
import Icon from "@mdi/react";
import {
  mdiWarehouse,
  mdiMapMarker,
  mdiCalendarMonth,
  mdiPackageVariant,
  mdiClose,
  mdiInformationOutline,
  mdiScriptTextPlayOutline,
  mdiTruckDeliveryOutline,
  mdiAccountOutline,
  mdiPhoneOutline,
  mdiTicketOutline,
  mdiToolboxOutline,
} from "@mdi/js";
import { FileText, ImageIcon, Download } from "lucide-react";
import { ReplacementSTOutward } from "../../model/warehouse_outward_model";
import DeviceIdsViewer from "../../common-components/DeviceIdsViewer";
import { useAuth } from "../../../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../../../utils/enums";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import {
  BizApproveReplacementST,
  BizRejectReplacementST,
  HwApproveReplacementST,
  HwRejectReplacementST,
  RetryReverseDC,
} from "../../warehouse_module_repo";
import LoadingWidget from "../../../../components/loading_spinner";
import SummaryItem from "./SummaryItem";

const getIcon = (type: string) => {
  if (type.includes("pdf")) return <FileText size={16} />;
  if (type.includes("image")) return <ImageIcon size={16} />;
  return <FileText size={16} />;
};

type Props = {
  open: boolean;
  onClose: () => void;
  outward: ReplacementSTOutward;
};

const formatAddress = (addr?: any) => {
  if (!addr) return "-";
  return `${addr.line1}, ${addr.city}, ${addr.state} - ${addr.pincode}`;
};

const ReplacementSTView: React.FC<Props> = ({ open, onClose, outward }) => {
  const { isAuthorised } = useAuth();
  const { showToast } = useToast();
  const [actioning, setActioning] = useState(false);
  const [rejectMode, setRejectMode] = useState<"hw" | "biz" | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  if (!open) return null;

  const {
    id: stoNo,
    status,
    from_warehouse_name: fromWarehouse,
    linked_order_id,
    complaint_id,
    service_id,
    customer_name,
    billing_address,
    shipping_address,
    expected_date,
    purpose,
    description,
    contact_name,
    contact_number,
    faulty_items = [],
    items = [],
  } = outward;

  // Cancelled STs must not expose finance details or any documents.
  const isCancelled = status === "Cancelled" || status === "Rejected";
  // Returned STs shouldn't show finance docs either, same as Cancelled.
  const hideFinanceDocs = isCancelled || status === "Returned";
  const dc_attachment = hideFinanceDocs ? undefined : outward.dc_attachment;
  const dc_ambill_attachment = hideFinanceDocs
    ? undefined
    : outward.dc_ambill_attachment;
  const eway_attachment = hideFinanceDocs ? undefined : outward.eway_attachment;
  const invoice_attachment = hideFinanceDocs
    ? undefined
    : outward.invoice_attachment;

  const allDeviceIds: string[] =
    items.reduce((acc: string[], item) => {
      if (
        item.subCategory === "Finished Goods" &&
        Array.isArray(item.deviceIds)
      ) {
        acc.push(...item.deviceIds);
      }
      return acc;
    }, []) ?? [];

  const totalValue =
    items.reduce((sum, i) => sum + (i.itemPrice || 0) * i.requestedQty, 0) || 0;

  const canHwApprove = isAuthorised({
    module: MODULE.replacement_st_hw_approval,
    action: [ACTION.UPDATE],
  });
  const canBizApprove = isAuthorised({
    module: MODULE.replacement_st_biz_approval,
    action: [ACTION.UPDATE],
  });

  const runAction = async (fn: () => Promise<any>, successText: string) => {
    setActioning(true);
    const res = await fn();
    setActioning(false);
    if (res?.success) {
      showToast({ type: ToastType.success, text: successText });
      setRejectMode(null);
      setRejectReason("");
      onClose();
    } else {
      showToast({
        type: ToastType.error,
        text: res?.message || res?.error || "Action failed.",
      });
    }
  };

  const submitReject = () => {
    if (!rejectReason.trim()) return;
    if (rejectMode === "hw") {
      runAction(
        () => HwRejectReplacementST(stoNo, rejectReason.trim()),
        "Rejected — sent back to Draft.",
      );
    } else if (rejectMode === "biz") {
      runAction(
        () => BizRejectReplacementST(stoNo, rejectReason.trim()),
        "Rejected — sent back to HW Approval Pending.",
      );
    }
  };

  return (
    <div
      id="warehouse-stock-transfer-replacement-view-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4 sm:p-8"
    >
      <LoadingWidget loading={actioning} />
      <div className="relative w-full sm:max-w-2xl max-h-[85vh] bg-white shadow-lg flex flex-col rounded-lg wh-modal-xs">
        {/* Header */}
        <div className="flex-shrink-0 flex items-start justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">{stoNo}</h2>

            <span className="rounded-full border px-2.5 py-0.5 text-xs font-medium">
              Replacement ST
            </span>

            <span className="rounded-full bg-blue-100 text-blue-700 px-2.5 py-0.5 text-xs font-medium">
              {status}
            </span>
          </div>

          <button
            id="warehouse-stock-transfer-replacement-view-modal-close-btn"
            onClick={onClose}
            className="rounded-md p-1 hover:bg-gray-100"
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <SummaryItem
              icon={mdiWarehouse}
              label="From Warehouse"
              value={fromWarehouse}
              color="text-blue-600"
            />

            {/* Destination block */}

            <>
              <SummaryItem
                icon={mdiMapMarker}
                label="Customer"
                value={customer_name}
                color="text-green-600"
              />
              <SummaryItem
                icon={mdiMapMarker}
                label="Billing Address"
                value={formatAddress(billing_address)}
                color="text-green-600"
              />
              <SummaryItem
                icon={mdiMapMarker}
                label="Shipping Address"
                value={formatAddress(shipping_address)}
                color="text-green-600"
              />
            </>

            <SummaryItem
              icon={mdiCalendarMonth}
              label="Expected Date"
              value={
                expected_date
                  ? new Date(expected_date).toISOString().split("T")[0]
                  : "-"
              }
              color="text-purple-600"
            />

            {!isCancelled && (
              <SummaryItem
                icon={mdiPackageVariant}
                label="Total Value"
                value={`₹${totalValue.toLocaleString()}`}
                color="text-orange-600"
              />
            )}

            {linked_order_id && (
              <SummaryItem
                icon={mdiInformationOutline}
                label="Order ID"
                value={linked_order_id}
                color="text-gray-600"
              />
            )}

            {complaint_id && (
              <SummaryItem
                icon={mdiTicketOutline}
                label="Complaint ID"
                value={complaint_id}
                color="text-teal-600"
              />
            )}

            {service_id && (
              <SummaryItem
                icon={mdiToolboxOutline}
                label="Service ID"
                value={service_id}
                color="text-slate-600"
              />
            )}

            <SummaryItem
              icon={mdiInformationOutline}
              label="Purpose"
              value={purpose}
              color="text-gray-600"
            />
            <SummaryItem
              icon={mdiScriptTextPlayOutline}
              label="Description"
              value={description}
              color="text-gray-500"
            />

            {contact_name && (
              <SummaryItem
                icon={mdiAccountOutline}
                label="Contact Name"
                value={contact_name}
                color="text-teal-600"
              />
            )}

            {contact_number && (
              <SummaryItem
                icon={mdiPhoneOutline}
                label="Contact Number"
                value={contact_number}
                color="text-slate-600"
              />
            )}

            {outward.shipping_details && (
              <>
                <SummaryItem
                  icon={mdiTruckDeliveryOutline}
                  label="Logistic Partner"
                  value={outward.shipping_details.logistic_partner}
                  color="text-indigo-600"
                />
                <SummaryItem
                  icon={mdiTruckDeliveryOutline}
                  label="Tracking ID"
                  value={outward.shipping_details.tracking_id}
                  color="text-indigo-600"
                />
              </>
            )}
          </div>

          {/* Attachment */}
          {dc_attachment && (
            <div className="border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  {getIcon(dc_attachment.type)}
                  <span className="truncate">{dc_attachment.filename}</span>
                </div>

                <a
                  id="warehouse-stock-transfer-replacement-view-modal-dc-attachment-link"
                  href={dc_attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                >
                  <Download size={14} />
                  View
                </a>
              </div>
            </div>
          )}
          {eway_attachment && (
            <div className="border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  {getIcon(eway_attachment.type)}
                  <span className="truncate">{eway_attachment.filename}</span>
                </div>

                <a
                  id="warehouse-stock-transfer-replacement-view-modal-eway-attachment-link"
                  href={eway_attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                >
                  <Download size={14} />
                  View
                </a>
              </div>
            </div>
          )}

          {invoice_attachment && (
            <div className="border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  {getIcon(invoice_attachment.type)}
                  <span className="truncate">
                    {invoice_attachment.filename}
                  </span>
                </div>

                <a
                  id="warehouse-stock-transfer-replacement-view-modal-invoice-attachment-link"
                  href={invoice_attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                >
                  <Download size={14} />
                  View
                </a>
              </div>
            </div>
          )}

          {dc_ambill_attachment && (
            <div className="border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  {getIcon(dc_ambill_attachment.type)}
                  <span className="truncate">
                    {dc_ambill_attachment.filename}
                  </span>
                </div>

                <a
                  id="warehouse-stock-transfer-replacement-view-modal-dc-ambill-attachment-link"
                  href={dc_ambill_attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                >
                  <Download size={14} />
                  View
                </a>
              </div>
            </div>
          )}

          {/* Grouped Attachments */}
          {!isCancelled &&
            outward.attachments &&
            outward.attachments.length > 0 && (
              <div className="space-y-4">
                {Object.entries(
                  outward.attachments.reduce(
                    (acc, file) => {
                      const category = file.category || "Other Attachments";
                      if (!acc[category]) acc[category] = [];
                      acc[category].push(file);
                      return acc;
                    },
                    {} as Record<string, typeof outward.attachments>,
                  ),
                ).map(([category, files]) => (
                  <div key={category} className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <FileText size={16} className="text-blue-600" />
                      {category}
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {files.map((file, idx) => (
                        <div
                          key={file.id || idx}
                          id={`warehouse-stock-transfer-replacement-view-modal-other-attachment-${file.id ?? idx}`}
                          className="flex items-center justify-between border rounded-lg bg-gray-50 px-3 py-2 text-sm"
                        >
                          <div className="flex items-center gap-2 truncate">
                            {getIcon(file.type)}
                            <span className="truncate">
                              {file?.name ?? `Attachment ${idx + 1}`}
                            </span>
                          </div>

                          <a
                            id={`warehouse-stock-transfer-replacement-view-modal-other-attachment-${file.id ?? idx}-view-link`}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                          >
                            <Download size={14} />
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

          {/* Released Devices */}
          {(status === "Rejected" || status === "Cancelled") &&
            allDeviceIds.length > 0 && (
              <DeviceIdsViewer
                deviceIds={allDeviceIds}
                title="Released Devices"
              />
            )}

          {(status === "Rejected" || status === "Cancelled") &&
            outward.rejection_reason && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
                <p className="font-semibold text-red-700">Finance Remark</p>
                <p className="mt-1 text-red-700">{outward.rejection_reason}</p>
              </div>
            )}

          {/* Items */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Replacement Items</h3>
            {items.map((item, idx) => (
              <div
                key={idx}
                id={`warehouse-stock-transfer-replacement-view-modal-item-row-${item.inventoryItemCode ?? idx}`}
                className="rounded-lg border p-4 space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{item.itemName}</p>
                    <p className="text-sm text-gray-500">
                      {item.inventoryItemCode}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Qty: {item.requestedQty}
                    </p>
                    {!isCancelled && (
                      <p className="text-xs text-gray-600 mt-1">
                        Unit Price: ₹{item.itemPrice?.toLocaleString()}
                      </p>
                    )}
                  </div>
                  {!isCancelled && (
                    <p className="font-medium">
                      ₹
                      {(
                        item.requestedQty * (item.itemPrice || 0)
                      ).toLocaleString()}
                    </p>
                  )}
                </div>

                {status !== "Rejected" &&
                  status !== "Cancelled" &&
                  item.subCategory === "Finished Goods" && (
                    <DeviceIdsViewer
                      deviceIds={item.deviceIds || []}
                      title="Allocated Devices"
                    />
                  )}
              </div>
            ))}
          </div>

          {/* Faulty Items */}
          {faulty_items && faulty_items.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Faulty Items</h3>
              {faulty_items.map((item, idx) => (
                <div
                  key={idx}
                  id={`warehouse-stock-transfer-replacement-view-modal-faulty-item-row-${item.inventoryItemCode ?? idx}`}
                  className="rounded-lg border p-4 space-y-3 bg-orange-50"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{item.itemName}</p>
                      <p className="text-sm text-gray-500">
                        {item.inventoryItemCode}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Qty: {item.requestedQty}
                      </p>
                      {!isCancelled && (
                        <p className="text-xs text-gray-600 mt-1">
                          Unit Price: ₹{item.itemPrice?.toLocaleString()}
                        </p>
                      )}
                    </div>
                    {!isCancelled && (
                      <p className="font-medium">
                        ₹
                        {(
                          item.requestedQty * (item.itemPrice || 0)
                        ).toLocaleString()}
                      </p>
                    )}
                  </div>

                  {item.subCategory === "Finished Goods" && (
                    <DeviceIdsViewer
                      deviceIds={item.deviceIds || []}
                      title="Faulty Devices"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Complaint-to-Replacement pipeline: two-stage approval actions */}
          {status === "HW Approval Pending" && canHwApprove && (
            <div
              id="warehouse-stock-transfer-replacement-view-modal-hw-approval-actions"
              className="rounded-lg border p-4 space-y-3 bg-yellow-50"
            >
              <p className="text-sm font-semibold">Hardware Approval</p>
              {rejectMode === "hw" ? (
                <RejectReasonBox
                  reason={rejectReason}
                  setReason={setRejectReason}
                  onCancel={() => setRejectMode(null)}
                  onSubmit={submitReject}
                  disabled={actioning}
                />
              ) : (
                <div className="flex gap-3">
                  <button
                    id="warehouse-stock-transfer-replacement-view-modal-hw-approve-btn"
                    type="button"
                    disabled={actioning}
                    onClick={() =>
                      runAction(
                        () => HwApproveReplacementST(stoNo),
                        "Approved — moved to Business Approval Pending.",
                      )
                    }
                    className="rounded bg-green-600 text-white px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    id="warehouse-stock-transfer-replacement-view-modal-hw-reject-btn"
                    type="button"
                    disabled={actioning}
                    onClick={() => setRejectMode("hw")}
                    className="rounded border border-red-400 text-red-600 px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          )}

          {status === "Business Approval Pending" && canBizApprove && (
            <div
              id="warehouse-stock-transfer-replacement-view-modal-biz-approval-actions"
              className="rounded-lg border p-4 space-y-3 bg-yellow-50"
            >
              <p className="text-sm font-semibold">Business Approval</p>
              {rejectMode === "biz" ? (
                <RejectReasonBox
                  reason={rejectReason}
                  setReason={setRejectReason}
                  onCancel={() => setRejectMode(null)}
                  onSubmit={submitReject}
                  disabled={actioning}
                />
              ) : (
                <div className="flex gap-3">
                  <button
                    id="warehouse-stock-transfer-replacement-view-modal-biz-approve-btn"
                    type="button"
                    disabled={actioning}
                    onClick={() =>
                      runAction(
                        () => BizApproveReplacementST(stoNo),
                        "Approved — moved to Device Allocation Pending.",
                      )
                    }
                    className="rounded bg-green-600 text-white px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    id="warehouse-stock-transfer-replacement-view-modal-biz-reject-btn"
                    type="button"
                    disabled={actioning}
                    onClick={() => setRejectMode("biz")}
                    className="rounded border border-red-400 text-red-600 px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          )}

          {outward.reverse_dc_creation_failed && canBizApprove && (
            <div
              id="warehouse-stock-transfer-replacement-view-modal-retry-reverse-dc"
              className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2"
            >
              <p className="text-sm font-semibold text-red-700">
                Reverse DC creation failed
              </p>
              {outward.reverse_dc_creation_error && (
                <p className="text-xs text-red-700">
                  {outward.reverse_dc_creation_error}
                </p>
              )}
              <button
                id="warehouse-stock-transfer-replacement-view-modal-retry-reverse-dc-btn"
                type="button"
                disabled={actioning}
                onClick={() =>
                  runAction(() => RetryReverseDC(stoNo), "Reverse DC created.")
                }
                className="rounded border px-3 py-1 text-sm disabled:opacity-50"
              >
                Retry Reverse DC
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RejectReasonBox = ({
  reason,
  setReason,
  onCancel,
  onSubmit,
  disabled,
}: {
  reason: string;
  setReason: (v: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  disabled: boolean;
}) => (
  <div className="space-y-2">
    <textarea
      id="warehouse-stock-transfer-replacement-view-modal-reject-reason"
      className="w-full border rounded p-2 text-sm"
      rows={2}
      placeholder="Reason for rejection (required)"
      value={reason}
      onChange={(e) => setReason(e.target.value)}
    />
    <div className="flex gap-3">
      <button
        id="warehouse-stock-transfer-replacement-view-modal-reject-submit-btn"
        type="button"
        disabled={disabled || !reason.trim()}
        onClick={onSubmit}
        className="rounded bg-red-600 text-white px-3 py-1 text-sm disabled:opacity-50"
      >
        Confirm Reject
      </button>
      <button
        id="warehouse-stock-transfer-replacement-view-modal-reject-cancel-btn"
        type="button"
        disabled={disabled}
        onClick={onCancel}
        className="rounded border px-3 py-1 text-sm disabled:opacity-50"
      >
        Cancel
      </button>
    </div>
  </div>
);

export default ReplacementSTView;
